import React from 'react';
import PropTypes from 'prop-types';
import ShortId from 'shortid';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import WithQuickSearch from '@hobs/hocs/WithQuickSearch';
import GridLoader from '@hobs/components/Grid/GridLoader';
import classnames from 'classnames';
import Columns from '@hobs/components/Grid/Columns';
import { CellDate } from '@hobs/components/Grid/CellDate';
import { RaisedButton, GhostButton } from '@hobs/components/Buttons';
import WithVisibleColumns from '@hobs/hocs/WithVisibleColumns';
import Icon from '@hobs/components/Icon';
import ObjectPath from 'object-path';
import Discussion from '@hobs/containers/Discussion';
import Status from '@hobs/components/Status';
import Toast from '@hobs/components/Toast';
import deepcopy from 'deepcopy';
import { H1 } from '@hobs/components/Headings';
import Dropdown from '@hobs/components/Dropdown';
import {
  ExcelExport,
  ExcelExportColumn
} from '@progress/kendo-react-excel-export';
import {
  withDataStateLocalOverrides,
  withGridLocalOverrides,
  onUserResetSettings
} from '@hobs/utils/grid/overrides';
import { Grid } from '@progress/kendo-react-grid';
import { parse } from '@hobs/utils/formatWithTimeZone';
import {
  PAGE_SIZES,
  USER_ROLES,
  TICKETS_EXPORT_FILE_NAME,
  TICKETS_STATUSES,
  SERVICE_REQUEST_NAVIGATION_ORIGIN,
  GRID_CUSTOM_FILTER_FIELDS
} from '@hobs/utils/constants';
import { createLoadingSelector } from '@hobs/utils/loading';
import { queryOData, resetOData, queryXLSOData } from '@hobs/actions/odata';
import WithRoles from '@hobs/hocs/WithRoles';
import { getTicketGrid } from '@hobs/actions/gridSelections';
import { ServiceRequestNavContext } from '@hobs/components/ServiceRequestNavContext';
import { getSorSummary } from '../../actions/sor';
import './Tickets.scss';

class Tickets extends React.PureComponent {
  LSdataStateName = `TicketsList${this.props.user.CurrentExerciseId}.dataState`;
  LSGridPropsName = `TicketsList${this.props.user.CurrentExerciseId}.grid.props`;
  constructor(props) {
    super(props);

    this.defaultGridColumns = this.getDefaultColumns();

    this.defaultDataState = {
      take: 20,
      skip: 0,
      sort: [{ field: 'TicketNumber', dir: 'desc' }]
    };

    this.state = {
      selectedUnit: '',
      dataState: withDataStateLocalOverrides(
        deepcopy(this.defaultDataState),
        this.LSdataStateName
      ),
      columns: withGridLocalOverrides(
        deepcopy(this.defaultGridColumns),
        this.LSGridPropsName
      )
    };

    this.exportRef = React.createRef();
    this.grid = React.createRef();
  }

  async componentDidMount() {
    const { getTicketGrid } = this.props;

    await this.props.getSorSummary();

    getTicketGrid().then(() => {
      const columns = this.getDefaultColumns();

      const columnsWithLocalOverrides = withGridLocalOverrides(
        deepcopy(columns),
        this.LSGridPropsName
      );

      this.setState({
        columns: {
          ...columnsWithLocalOverrides
        }
      });

      this.props.withVisibleColumns.setColumns(this.state.columns);
      this.props.withVisibleColumns.onUpdate(() => {
        this.setState({
          gridKey: ShortId.generate()
        });
      });
    });

    this.loadODataQuery();

    window.addEventListener('scroll', this.handleScroll);
    window.addEventListener('resize', this.handleScroll);
  }

  componentWillUnmount() {
    this.props.resetOData();
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleScroll);
  }

  onUserSaveSettings = () => {
    localStorage.setItem(
      this.LSdataStateName,
      JSON.stringify(this.state.dataState)
    );
    localStorage.setItem(
      this.LSGridPropsName,
      JSON.stringify({
        columns: this.grid.current.columns
      })
    );
  };

  onUserResetSettings = onUserResetSettings(
    this.LSdataStateName,
    this.LSGridPropsName,
    null,
    () => {
      const columns = this.getDefaultColumns();

      this.props.withVisibleColumns.setColumns(columns);

      this.setState(
        {
          gridKey: ShortId.generate(),
          columns,
          dataState: this.defaultDataState
        },
        () => {
          this.loadODataQuery();
        }
      );
    }
  );

  handleScroll = () => {
    document.getElementsByClassName('k-grid').forEach(grid => {
      const header = grid.getElementsByClassName('k-grid-header');
      if (!header.length) {
        return;
      }

      if (window.pageYOffset >= grid.offsetTop) {
        if (!header[0].classList.contains('fixed-header')) {
          header[0].classList.add('fixed-header');
        }
        header[0].style.maxWidth = `${grid.offsetWidth}px`;
      } else {
        if (header[0].classList.contains('fixed-header')) {
          header[0].classList.remove('fixed-header');
          header[0].style.maxWidth = '';
        }
      }
    });
  };

  dataStateChange = e => {
    this.setState(
      {
        dataState: e.dataState
      },
      () => {
        this.loadODataQuery();
      }
    );
  };

  loadODataQuery = () => {
    this.props.queryOData('TicketsQuery', this.getDataState());
  };

  getDataState() {
    let filters = [];

    if (Object.keys(this.props.withQuickSearchFilter).length > 0) {
      filters.push(this.props.withQuickSearchFilter);
    }

    if (this.state.selectedUnit && this.state.selectedUnit != 'All units') {
      filters.push({
        field: 'UnitId',
        operator: 'eq',
        value: `<guid>${this.state.selectedUnit}</guid>`
      });
    }

    const dataState = {
      ...this.state.dataState
    };

    if (
      this.state.dataState.filter &&
      this.state.dataState.filter.filters.length > 0
    ) {
      filters = filters.concat(this.state.dataState.filter);
    }

    if (filters.length > 0) {
      dataState.filter = {
        logic: 'and',
        filters
      };
    }

    return dataState;
  }

  onQuickSearch = quickSearchValue => {
    const { preFilledQuickSearch, dataState } = this.state;
    this.setState(
      {
        dataState: {
          ...dataState,
          skip: preFilledQuickSearch ? dataState.skip : 0
        },
        quickSearchValue,
        preFilledQuickSearch: null
      },
      () => {
        const dataState = this.getDataState();
        this.props.queryOData('TicketsQuery', dataState);
      }
    );
  };

  getDiscussionPath(srId, ticketNumber) {
    const { match } = this.props;
    return `${match.url}/${srId}/${ticketNumber}/discussion`;
  }

  getDefaultColumns() {
    const { t, hasUserPermission, ticketGrid } = this.props;
    const columns = {
      TicketNumber: {
        title: t('Ticket No.'),
        columnMenu: true,
        default: true,
        visible: true,
        filter: 'numeric',
        cell: ({ dataItem }) => (
          <td>
            {!hasUserPermission(USER_ROLES.READONLY) &&
              !hasUserPermission(USER_ROLES.HOST_UNIT) && (
                <ServiceRequestNavContext.Consumer>
                  {nav => (
                    <Link
                      to={`/service-request/${dataItem.ServiceRequestId}/change-history`}
                      onClick={() => {
                        nav.setPreviousPageHistory(history);
                        nav.setPreviousGridPosition(
                          this.state.dataState,
                          window.pageYOffset,
                          this.state.selectedAnnex,
                          this.state.quickSearchValue,
                          SERVICE_REQUEST_NAVIGATION_ORIGIN.Tickets,
                          this.state.selectedUnit
                        );
                        nav.setTicketNumber(dataItem.TicketNumber);
                        nav.setServiceRequestId(dataItem.ServiceRequestId);
                      }}
                    >
                      {dataItem.TicketNumber}
                    </Link>
                  )}
                </ServiceRequestNavContext.Consumer>
              )}
            {(hasUserPermission(USER_ROLES.READONLY) ||
              hasUserPermission(USER_ROLES.HOST_UNIT)) && (
              <>{dataItem.TicketNumber}</>
            )}
          </td>
        )
      },
      AdditionalRequestStatusName: {
        title: t('Status'),
        default: true,
        visible: true,
        width: 103,
        cell: ({ dataItem }) => (
          <td colSpan="1">
            <Status
              type={parseInt(dataItem.AdditionalRequestStatusId, 10)}
              name={dataItem.AdditionalRequestStatusName}
              inverted={true}
              statuses={TICKETS_STATUSES}
            />
          </td>
        ),
        columnMenu: true,
        customFilter: {
          data: [
            {
              name: t('Select all'),
              default: true,
              field: 'AdditionalRequestStatusId',
              value: ''
            },
            ...ticketGrid.additionalRequestsStatuses.map(item => ({
              value: `<guid>${item.id}</guid>`,
              name: item.title,
              field: 'AdditionalRequestStatusId',
              default: false
            }))
          ]
        }
      },
      CreationDate: {
        title: t('Creation date'),
        columnMenu: true,
        default: true,
        visible: true,
        filter: 'date',
        cell: CellDate
      },
      RequestType: {
        title: t('Req. type'),
        columnMenu: true,
        default: true,
        visible: true,
        customFilter: {
          data: [
            {
              name: t('Select all'),
              default: true,
              field: 'RequestTypeId',
              value: ''
            },
            ...ticketGrid.additionalRequestTypes.map(item => ({
              value: `<guid>${item.id}</guid>`,
              name: item.title,
              field: 'RequestTypeId',
              default: false
            }))
          ]
        }
      },
      RequestNumber: {
        title: t('Req. No.'),
        columnMenu: true,
        default: true,
        visible: true,
        cell: ({ dataItem }) => (
          <td>
            {!hasUserPermission(USER_ROLES.READONLY) &&
              !hasUserPermission(USER_ROLES.HOST_UNIT) &&
              (dataItem.IsCustomServiceRequest ? (
                <ServiceRequestNavContext.Consumer>
                  {nav => (
                    <Link
                      to={`/custom-service-request/${dataItem.ServiceRequestId}`}
                      onClick={() => {
                        nav.setPreviousPageHistory(history);
                        nav.setPreviousGridPosition(
                          this.state.dataState,
                          window.pageYOffset,
                          this.state.selectedAnnex,
                          this.state.quickSearchValue,
                          SERVICE_REQUEST_NAVIGATION_ORIGIN.Tickets,
                          this.state.selectedUnit
                        );
                        nav.setTicketNumber(dataItem.TicketNumber);
                        nav.setServiceRequestId(dataItem.ServiceRequestId);
                      }}
                    >
                      {dataItem.RequestNumber}
                    </Link>
                  )}
                </ServiceRequestNavContext.Consumer>
              ) : (
                <ServiceRequestNavContext.Consumer>
                  {nav => (
                    <Link
                      to={`/service-request/${dataItem.ServiceRequestId}`}
                      onClick={() => {
                        nav.setPreviousPageHistory(history);
                        nav.setPreviousGridPosition(
                          this.state.dataState,
                          window.pageYOffset,
                          this.state.selectedAnnex,
                          this.state.quickSearchValue,
                          SERVICE_REQUEST_NAVIGATION_ORIGIN.Tickets,
                          this.state.selectedUnit
                        );
                        nav.setTicketNumber(dataItem.TicketNumber);
                        nav.setServiceRequestId(dataItem.ServiceRequestId);
                      }}
                    >
                      {dataItem.RequestNumber}
                    </Link>
                  )}
                </ServiceRequestNavContext.Consumer>
              ))}
            {(hasUserPermission(USER_ROLES.READONLY) ||
              hasUserPermission(USER_ROLES.HOST_UNIT)) && (
              <>{dataItem.RequestNumber}</>
            )}
          </td>
        )
      },
      AnnexInformation: {
        title: t('Annex information'),
        columnMenu: true,
        default: true,
        visible: true
      },
      ServiceName: {
        title: t('Service'),
        columnMenu: true,
        default: true,
        visible: true
      },
      Unit: {
        title: t('Unit'),
        columnMenu: true,
        default: true,
        visible: true
      },
      LocationName: {
        title: t('Location'),
        columnMenu: true,
        default: true,
        visible: true,
        customFilter: {
          data: [
            {
              name: t('Select all'),
              default: true,
              field: 'LocationName',
              value: ''
            },
            ...this.getColumnFilterByNameValues('locations')
          ]
        }
      },
      RegionId: {
        title: t('Region'),
        columnMenu: true,
        default: false,
        visible: false,
        customFilter: {
          filter: 'contains',
          data: [
            {
              name: t('Select all'),
              default: true,
              value: ''
            },
            ...this.getColumnFilterValuesAsString(
              GRID_CUSTOM_FILTER_FIELDS.regions,
              'RegionIds'
            )
          ]
        },
        cell: ({ dataItem }) => {
          return <td>{dataItem.RegionName}</td>;
        }
      },
      DeliveryFromDate: {
        title: t('From date'),
        columnMenu: true,
        default: true,
        visible: true,
        filter: 'date',
        cell: CellDate
      },
      DeliveryToDate: {
        title: t('To date'),
        columnMenu: true,
        default: true,
        visible: true,
        filter: 'date',
        cell: CellDate
      },
      LastUpdate: {
        title: t('Last update'),
        columnMenu: true,
        default: false,
        visible: false,
        filter: 'date',
        cell: CellDate
      },
      HasAttachments: {
        width: '33px',
        title: t('Attachment'),
        cell: ({ dataItem }) => (
          <td colSpan="1">
            {dataItem.HasAttachments && (
              <Icon icon="attachment" className="tickets-view--icon" />
            )}
          </td>
        ),
        headerCell: () => (
          <Icon icon="attachment" className="tickets-view--icon" />
        ),
        default: true,
        visible: true,
        xls: false
      }
    };

    if (
      !hasUserPermission(USER_ROLES.READONLY) &&
      !hasUserPermission(USER_ROLES.HOST_UNIT)
    ) {
      Object.assign(columns, {
        DiscussionStatus: {
          title: t('Disc.'),
          default: true,
          columnMenu: true,
          visible: true,
          xls: false,
          width: '50px',
          cell: ({ dataItem }) => {
            return (
              <td>
                <Link
                  to={this.getDiscussionPath(
                    dataItem.ServiceRequestId,
                    dataItem.TicketNumber
                  )}
                >
                  <Discussion
                    serviceRequestId={dataItem.ServiceRequestId}
                    status={dataItem.DiscussionStatus}
                    iconOnly={true}
                    initials={t('HN')}
                    ticketNumber={dataItem.TicketNumber}
                    onDiscussionClose={this.loadODataQuery}
                    isSeen={dataItem.IsLastDiscussionSeen}
                  />
                </Link>
              </td>
            );
          },
          customFilter: {
            data: [
              {
                name: t('Select all'),
                default: true,
                value: ''
              },
              {
                name: t('Unread messages'),
                value: 1
              },
              {
                name: t('Read messages'),
                value: 2
              },
              {
                name: t('No messages'),
                value: 3
              }
            ]
          }
        }
      });
    }

    return columns;
  }

  async getXlsData() {
    const dataState = this.getDataState();
    delete dataState.take;
    dataState.skip = 0;
    const XLSoData = await this.props.queryXLSOData('TicketsQuery', dataState);

    const data = XLSoData.data.map(item => {
      item.StatusId = item.StatusGridName;
      item.DeliveryFromDate = parse(item.DeliveryFromDate);
      item.DeliveryToDate = parse(item.DeliveryToDate);
      item.CreationDate = parse(item.CreationDate);
      item.LastUpdate = parse(item.LastUpdate);
      return item;
    });

    this.setState({
      XLSoData: data
    });
  }

  exportTickets = async () => {
    await this.getXlsData();
    this.exportRef.current.save();
  };

  getColumns() {
    const {
      withVisibleColumns: { getGridColumns }
    } = this.props;
    return Columns({
      columns: getGridColumns,
      onSave: this.onUserSaveSettings,
      onReset: this.onUserResetSettings,
      dataState: this.state.dataState
    });
  }
  canCreateServiceRequest = () => {
    const { sor } = this.props;
    const { isCreateSrDisabled } = this.state;
    return (
      (!!ObjectPath.get(sor, 'sorSummary.canCreateAdditionalServiceRequest') ||
        !!ObjectPath.get(sor, 'sorSummary.isExerciseIsFrozen')) &&
      !isCreateSrDisabled
    );
  };

  getColumnFilterValuesAsString(gridFilterFieldName, field) {
    const { ticketGrid } = this.props;

    if (ticketGrid && ticketGrid[gridFilterFieldName]) {
      return ticketGrid[gridFilterFieldName].map(filterValue => {
        const filter = {
          name: filterValue.title,
          value: filterValue.id
        };

        if (field) {
          filter.field = field;
        }
        return filter;
      });
    }

    return [];
  }

  getColumnFilterByNameValues(gridFilterFieldName) {
    const { ticketGrid } = this.props;

    if (ticketGrid && ticketGrid[gridFilterFieldName]) {
      return ticketGrid[gridFilterFieldName].map(filterValue => {
        const value = filterValue.title ? filterValue.title : filterValue;

        return {
          name: value,
          value: value
        };
      });
    }

    return [];
  }

  flattenUnitsTree(array, level = 0) {
    if (!Array.isArray(array)) {
      return [];
    }

    const units = array.reduce((result, item) => {
      result.push({
        unitName: `${'\xa0\xa0\xa0'.repeat(level)}${item.unitName}`,
        value: item.unitName,
        unitId: item.unitId
      });

      if (Array.isArray(item.units)) {
        result = result.concat(this.flattenUnitsTree(item.units, level + 1));
      }

      return result;
    }, []);

    return units;
  }

  getUnitOptions = unitsTree => {
    const { t } = this.props;

    const units = this.flattenUnitsTree(unitsTree);

    if (units && units.length) {
      return []
        .concat(
          [
            {
              unitName: t('All units'),
              value: ''
            }
          ],
          units
        )
        .map(unit => {
          return (
            <option key={ShortId.generate()} value={unit.unitId}>
              {unit.unitName}
            </option>
          );
        });
    }

    return (
      <option value="">
        {t(
          'There are no SN units added for this Exercise. Please add them in SOR requisites'
        )}
      </option>
    );
  };

  handleUnitFilterChange = value => {
    this.setState(
      {
        selectedUnit: value,
        dataState: {
          ...this.state.dataState,
          skip: 0
        }
      },
      () => {
        this.loadODataQuery();
      }
    );
  };

  navigateToSavedLocation(nav) {
    if (
      nav.previousGridPosition.windowScrollOffset &&
      nav.previousGridPosition.navigationOrigin ===
        SERVICE_REQUEST_NAVIGATION_ORIGIN.Tickets &&
      this.state.previousWindowScrollOffset !==
        nav.previousGridPosition.windowScrollOffset
    ) {
      this.setState({
        previousWindowScrollOffset: nav.previousGridPosition.windowScrollOffset
      });
      nav.clearPreviousPosition();
    }
  }

  setSavedDataState(nav) {
    const newState = {};
    if (
      nav.previousGridPosition.dataState &&
      nav.previousGridPosition.navigationOrigin !==
        SERVICE_REQUEST_NAVIGATION_ORIGIN.Tickets
    ) {
      nav.clearPreviousPosition();
    }

    if (
      nav.previousGridPosition.dataState &&
      nav.previousGridPosition.navigationOrigin ===
        SERVICE_REQUEST_NAVIGATION_ORIGIN.Tickets &&
      JSON.stringify(nav.previousGridPosition.dataState) !==
        JSON.stringify(this.state.dataState)
    ) {
      Object.assign(newState, {
        dataState: nav.previousGridPosition.dataState,
        previousWindowScrollOffset: nav.previousGridPosition.windowScrollOffset
      });
    }
    if (
      nav.previousGridPosition.savedQuickSearch &&
      JSON.stringify(nav.previousGridPosition.savedQuickSearch) !==
        JSON.stringify(this.state.preFilledQuickSearch)
    ) {
      Object.assign(newState, {
        preFilledQuickSearch: nav.previousGridPosition.savedQuickSearch
      });
      nav.clearSavedQuickSearch();
    }
    if (
      nav.previousGridPosition.selectedUnit &&
      nav.previousGridPosition.navigationOrigin ===
        SERVICE_REQUEST_NAVIGATION_ORIGIN.Tickets
    ) {
      Object.assign(newState, {
        selectedUnit: nav.previousGridPosition.selectedUnit
      });
      nav.clearSelectedUnit();
    }

    if (JSON.stringify(newState) !== JSON.stringify({})) {
      this.setState(newState, () => {
        this.loadODataQuery();
      });
    }

    if (nav.previousGridPosition && nav.previousGridPosition.dataState) {
      nav.clearSavedDataState();
    }
  }

  clearSavedOffset() {
    if (
      this.state.previousWindowScrollOffset &&
      window.pageYOffset === this.state.previousWindowScrollOffset
    ) {
      this.setState({
        previousWindowScrollOffset: null
      });
    }
  }

  scrollWindow() {
    this.state.previousWindowScrollOffset &&
      window.scrollTo(0, this.state.previousWindowScrollOffset);
  }

  render() {
    const {
      t,
      hasUserPermission,
      sor,
      withVisibleColumns: { getVisibleColumnCount, openDialog, getGridColumns },
      isLoading
    } = this.props;
    const { selectedUnit } = this.state;

    return (
      <div className="tickets-view">
        {this.scrollWindow()}
        {this.clearSavedOffset()}
        <ServiceRequestNavContext.Consumer>
          {nav => !isLoading && this.navigateToSavedLocation(nav)}
        </ServiceRequestNavContext.Consumer>
        <ServiceRequestNavContext.Consumer>
          {nav => this.setSavedDataState(nav)}
        </ServiceRequestNavContext.Consumer>
        <ServiceRequestNavContext.Consumer>
          {nav =>
            nav.serviceRequestNavigation !==
              SERVICE_REQUEST_NAVIGATION_ORIGIN.Tickets &&
            nav.setServiceRequestNavigation(
              SERVICE_REQUEST_NAVIGATION_ORIGIN.Tickets
            )
          }
        </ServiceRequestNavContext.Consumer>
        <H1>{t('Additional requests')}</H1>
        <div>
          <div className="tickets-view--header">
            <div className="tickets-view--left">
              {!hasUserPermission(USER_ROLES.READONLY) &&
                !hasUserPermission(USER_ROLES.HOST_UNIT) && (
                  <RaisedButton
                    disabled={!this.canCreateServiceRequest()}
                    to="/service-request"
                    type="button"
                    key={ShortId.generate()}
                    iconLeft={<Icon icon="Plus" />}
                    onClick={event => {
                      if (
                        ObjectPath.get(sor, 'sorSummary.isExerciseIsFrozen')
                      ) {
                        event.preventDefault();
                        Toast.warning(
                          t(
                            'New services cannot be added, because FSOR is currently frozen'
                          )
                        );
                      }
                    }}
                  >
                    {t('Add additional request')}
                  </RaisedButton>
                )}
            </div>
            <div className="tickets-view--header--controls">
              <div className="service-requests-view--heading-filters">
                <ServiceRequestNavContext.Consumer>
                  {nav => (
                    <Dropdown
                      wrapperClass="service-requests-view--heading-filters-dropdown"
                      large
                      value={selectedUnit}
                      onChange={event => {
                        nav.clearSelectedUnit();
                        this.handleUnitFilterChange(event.target.value);
                      }}
                    >
                      {this.getUnitOptions(
                        ObjectPath.get(this.props.sor, 'sorSummary.units')
                      )}
                    </Dropdown>
                  )}
                </ServiceRequestNavContext.Consumer>
              </div>
              <div>
                {this.props.withQuickSearchForm({
                  onSearch: this.onQuickSearch,
                  preFilledQuickSearch: this.state.preFilledQuickSearch,
                  noMargin: true
                })}
              </div>
              <GhostButton
                onClick={() => {
                  openDialog();
                }}
                type="button"
              >
                {t('Visible columns')} ({getVisibleColumnCount()})
              </GhostButton>
              <GhostButton
                iconLeft={<Icon icon="download" />}
                onClick={() => {
                  this.exportTickets();
                }}
              >
                {t('XLSX')}
              </GhostButton>
            </div>
          </div>
          {Object.keys(getGridColumns).length > 0 && (
            <>
              <ExcelExport
                ref={this.exportRef}
                data={this.state.XLSoData}
                fileName={TICKETS_EXPORT_FILE_NAME}
              >
                {Object.keys(getGridColumns).reduce((results, key) => {
                  const column = getGridColumns[key];
                  if (
                    (typeof column.xls === 'undefined' || column.xls) &&
                    column.visible
                  ) {
                    if (column.filter === 'date') {
                      results.push(
                        <ExcelExportColumn
                          key={ShortId.generate()}
                          width={125}
                          field={column.xlsField || key}
                          title={column.title}
                        />
                      );
                    } else if (column.title === 'Region') {
                      results.push(
                        <ExcelExportColumn
                          key={ShortId.generate()}
                          width={125}
                          field={column.xlsField || 'RegionName'}
                          title={column.title}
                        />
                      );
                    } else {
                      results.push(
                        <ExcelExportColumn
                          key={ShortId.generate()}
                          field={column.xlsField || key}
                          title={column.title}
                        />
                      );
                    }
                  }
                  return results;
                }, [])}
              </ExcelExport>
              <ServiceRequestNavContext.Consumer>
                {nav => (
                  <Grid
                    sortable={true}
                    resizable={true}
                    key={this.state.gridKey}
                    ref={this.grid}
                    pageable={{
                      pageSizes: PAGE_SIZES
                    }}
                    {...this.state.dataState}
                    {...this.props.oData}
                    onDataStateChange={this.dataStateChange}
                    rowRender={(row, props) => {
                      return {
                        ...row,
                        props: {
                          ...row.props,
                          className: classnames(row.props.className, {
                            'tickets-view--last-visited':
                              props.dataItem.ServiceRequestId ===
                                nav.serviceRequestId &&
                              props.dataItem.TicketNumber === nav.ticketNumber
                          })
                        }
                      };
                    }}
                  >
                    {this.getColumns()}
                  </Grid>
                )}
              </ServiceRequestNavContext.Consumer>
            </>
          )}
          {this.props.isLoading && <GridLoader />}
        </div>
      </div>
    );
  }
}

Tickets.propTypes = {
  isLoading: PropTypes.bool,
  oData: PropTypes.object,
  queryOData: PropTypes.func,
  resetOData: PropTypes.func,
  loadODataQuery: PropTypes.func,
  location: PropTypes.object,
  match: PropTypes.object,
  history: PropTypes.object,
  t: PropTypes.func,
  withQuickSearchForm: PropTypes.func,
  withQuickSearchFilter: PropTypes.object,
  getGroupGrid: PropTypes.func,
  hasUserPermission: PropTypes.func,
  queryXLSOData: PropTypes.func,
  sor: PropTypes.object,
  forSubmit: PropTypes.object,
  getTicketGrid: PropTypes.func,
  getSorSummary: PropTypes.func,
  ticketGrid: PropTypes.object,
  withVisibleColumns: PropTypes.object,
  user: PropTypes.object
};

const oDataLoading = createLoadingSelector(['OData']);
const mapStateToProps = state => ({
  isLoading: oDataLoading(state),
  oData: state.odata,
  sor: state.sor,
  forSubmit: state.tickets.forSubmit,
  ticketGrid: state.gridSelections.ticketGrid
});

const mapDispatchToProps = {
  queryOData,
  resetOData,
  queryXLSOData,
  getSorSummary,
  getTicketGrid
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  withTranslation()(
    withRouter(WithVisibleColumns(WithQuickSearch(WithRoles(Tickets))))
  )
);
